import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, output, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Avatar } from '../avatar/avatar';
import { ZagIcon } from '../icon/icon';
import { Badge, type BadgeColor } from '../badge/badge';
import { ColorContrastService } from '../services/color-contrast.service';

/**
 * Stat item displayed in the profile summary card
 */
export interface ProfileStat {
  /** Stat label */
  label: string;
  /** Stat value (string for flexibility) */
  value: string | number;
  /** Material icon name */
  icon: string;
}

/**
 * Quick action displayed in the profile summary card
 */
export interface ProfileQuickAction {
  /** Unique identifier */
  id: string;
  /** Display label */
  label: string;
  /** Material icon name */
  icon: string;
  /** Whether the action is disabled */
  disabled?: boolean;
  /** Whether to render the button with error/danger styling */
  danger?: boolean;
}

/**
 * Danger zone action (e.g., delete)
 */
export interface ProfileDangerAction {
  /** Unique identifier */
  id: string;
  /** Display label */
  label: string;
  /** Material icon name */
  icon: string;
  /** Whether the action is disabled */
  disabled?: boolean;
}

/**
 * Profile Summary Card component.
 *
 * Displays a summary panel with multiple cards inside a container:
 * - Profile card: Avatar and name (with optional edit hover)
 * - Stats card: Quick stats (configurable)
 * - Quick actions card
 * - Danger zone card
 *
 * Used in entity detail pages (person, team, etc.) as a sidebar.
 *
 * @example
 * ```html
 * <zag-profile-summary-card
 *   [avatarName]="'John Doe'"
 *   [avatarSrc]="person.profilePicture"
 *   [subtitle]="'Employee'"
 *   [stats]="stats"
 *   [quickActions]="quickActions"
 *   [dangerActions]="dangerActions"
 *   (quickActionClick)="onQuickAction($event)"
 *   (dangerActionClick)="onDangerAction($event)"
 *   (editClick)="onEditProfile()"
 * />
 * ```
 */
@Component({
  selector: 'zag-profile-summary-card',
  imports: [Avatar, ZagIcon, FormsModule, Badge],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <aside class="profile-summary" [class.profile-summary--compact]="compact()">
      <!-- Profile Card -->
      <div class="profile-summary__card profile-summary__profile">
        <!-- Corner actions slot (e.g. collapse button) -->
        <div class="profile-summary__corner">
          <ng-content select="[profile-corner]" />
        </div>
        <!-- Avatar with edit overlay -->
        <div
          class="profile-summary__avatar-wrapper"
          [class.profile-summary__avatar-wrapper--editable]="editable()"
          [class.profile-summary__avatar-wrapper--icon]="avatarIcon()"
          [class.profile-summary__avatar-wrapper--icon-fallback]="avatarIcon() && !avatarIconColor()"
          [class.profile-summary__avatar-wrapper--badged]="avatarBadge()"
          [style.background-color]="iconBackgroundColor()"
          (click)="editable() && editClick.emit()"
          (keydown.enter)="editable() && editClick.emit()"
          [attr.tabindex]="editable() ? 0 : null"
          [attr.role]="editable() ? 'button' : null"
          [attr.aria-label]="editable() ? 'Change avatar' : null"
        >
          @if (avatarIcon()) {
            <span
              [zagIcon]="avatarIcon()!"
              class="profile-summary__icon-avatar"
              [style.--zag-appearance-preview-icon-color]="iconTextColor()"
            ></span>
          } @else {
            <zag-avatar [name]="avatarName()" [src]="avatarSrc()" size="xl" />
          }
          @if (editable()) {
            <div class="profile-summary__edit-overlay">
              <span zagIcon="edit"></span>
            </div>
          }
          @if (avatarBadge()) {
            <zag-badge class="profile-summary__avatar-badge" [color]="avatarBadgeColor()" [solid]="true">
              {{ avatarBadge() }}
            </zag-badge>
          }
        </div>
        <div class="profile-summary__info">
          @if (isEditingName()) {
            <input
              #nameInput
              class="profile-summary__name-input"
              type="text"
              [value]="avatarName()"
              (keydown.enter)="commitNameEdit(nameInput.value)"
              (keydown.escape)="cancelNameEdit()"
              (blur)="commitNameEdit(nameInput.value)"
              aria-label="Edit name"
            />
          } @else {
            <h2
              class="profile-summary__name"
              [class.profile-summary__name--editable]="editableName()"
              (click)="editableName() && startNameEdit()"
              (keydown.enter)="editableName() && startNameEdit()"
              [attr.tabindex]="editableName() ? 0 : null"
              [attr.role]="editableName() ? 'button' : null"
            >
              {{ avatarName() }}
            </h2>
          }
          @if (isEditingSubtitle()) {
            <input
              #subtitleInput
              class="profile-summary__subtitle-input"
              type="text"
              [value]="subtitle() ?? ''"
              (keydown.enter)="commitSubtitleEdit(subtitleInput.value)"
              (keydown.escape)="cancelSubtitleEdit()"
              (blur)="commitSubtitleEdit(subtitleInput.value)"
              aria-label="Edit subtitle"
            />
          } @else if (subtitle() || editableSubtitle()) {
            <p
              class="profile-summary__subtitle"
              [class.profile-summary__subtitle--editable]="editableSubtitle()"
              (click)="editableSubtitle() && startSubtitleEdit()"
              (keydown.enter)="editableSubtitle() && startSubtitleEdit()"
              [attr.tabindex]="editableSubtitle() ? 0 : null"
              [attr.role]="editableSubtitle() ? 'button' : null"
            >
              {{ subtitle() || subtitlePlaceholder() }}
            </p>
          }
        </div>

        <!-- Slot for extra content below the header (e.g. embedded section nav). -->
        <ng-content select="[profile-extra]" />
      </div>

      <!-- Stats Card -->
      @if (stats().length > 0) {
        <div class="profile-summary__card profile-summary__stats">
          @for (stat of stats(); track stat.label) {
            <div class="profile-summary__stat">
              <div class="profile-summary__stat-icon-wrapper">
                <span [zagIcon]="stat.icon" class="profile-summary__stat-icon"></span>
              </div>
              <div class="profile-summary__stat-content">
                <span class="profile-summary__stat-value">{{ stat.value }}</span>
                <span class="profile-summary__stat-label">{{ stat.label }}</span>
              </div>
            </div>
          }
        </div>
      }

      <!-- Extra summary cards (e.g., balance overview) -->
      <ng-content select="[summary-cards]" />

      <!-- Quick Actions Card -->
      @if (quickActions().length > 0) {
        <div class="profile-summary__card profile-summary__actions">
          <h3 class="profile-summary__section-title">
            {{ quickActionsTitle() }}
          </h3>
          @for (action of quickActions(); track action.id) {
            <button
              class="profile-summary__action-btn"
              [class.profile-summary__action-btn--danger]="action.danger"
              type="button"
              [disabled]="action.disabled"
              (click)="quickActionClick.emit(action)"
            >
              <span [zagIcon]="action.icon" aria-hidden="true"></span>
              <span>{{ action.label }}</span>
            </button>
          }
          <div class="profile-summary__actions-extra">
            <ng-content select="[quick-actions-extra]" />
          </div>
        </div>
      }

      <!-- Danger Zone Card -->
      @if (dangerActions().length > 0) {
        <div class="profile-summary__card profile-summary__danger">
          <h3 class="profile-summary__section-title">
            {{ dangerZoneTitle() }}
          </h3>
          @for (action of dangerActions(); track action.id) {
            <button class="profile-summary__danger-btn" type="button" [disabled]="action.disabled" (click)="dangerActionClick.emit(action)">
              <span [zagIcon]="action.icon" aria-hidden="true"></span>
              <span>{{ action.label }}</span>
            </button>
          }
        </div>
      }
    </aside>
  `,
  styleUrl: './profile-summary-card.component.scss',
})
export class ProfileSummaryCardComponent {
  private readonly colorContrast = inject(ColorContrastService);

  /** Name to display (also used for avatar initials) */
  readonly avatarName = input.required<string>();

  /** Optional avatar image URL */
  readonly avatarSrc = input<string | undefined>(undefined);

  /** Optional icon name (instead of photo - for teams, skills, etc.) */
  readonly avatarIcon = input<string | undefined>(undefined);

  /** Optional icon color (used with avatarIcon) */
  readonly avatarIconColor = input<string | undefined>(undefined);

  /** Optional badge label rendered over the bottom of the avatar */
  readonly avatarBadge = input<string | undefined>(undefined);

  /** Color of the avatar badge — defaults to 'neutral' */
  readonly avatarBadgeColor = input<BadgeColor>('neutral');

  readonly iconBackgroundColor = computed(() => {
    return this.avatarIconColor() ?? null;
  });

  readonly iconTextColor = computed(() => {
    return this.colorContrast.getIconColor(this.avatarIconColor());
  });

  /** Subtitle (e.g., "Employee", role, etc.) */
  readonly subtitle = input<string | undefined>(undefined);

  /** Placeholder text shown when subtitle is empty and editableSubtitle is true */
  readonly subtitlePlaceholder = input<string>('');

  /** Whether the profile header is editable (shows edit overlay on hover) */
  readonly editable = input<boolean>(false);

  /** Whether the name is inline-editable (click to edit) */
  readonly editableName = input<boolean>(false);

  /** Whether the subtitle is inline-editable (click to edit) */
  readonly editableSubtitle = input<boolean>(false);

  /** Stats to display */
  readonly stats = input<ProfileStat[]>([]);

  /** Compact density for tighter sidebars (reduced paddings and gaps) */
  readonly compact = input<boolean>(false);

  /** Quick actions title */
  readonly quickActionsTitle = input<string>('Quick Actions');

  /** Quick actions to display */
  readonly quickActions = input<ProfileQuickAction[]>([]);

  /** Danger zone title */
  readonly dangerZoneTitle = input<string>('Danger Zone');

  /** Danger zone actions */
  readonly dangerActions = input<ProfileDangerAction[]>([]);

  /** Emitted when the name is changed via inline editing */
  readonly nameChange = output<string>();

  /** Emitted when the subtitle is changed via inline editing */
  readonly subtitleChange = output<string>();

  /** Internal editing state */
  protected readonly isEditingName = signal(false);
  protected readonly isEditingSubtitle = signal(false);

  private readonly nameInputRef = viewChild<ElementRef<HTMLInputElement>>('nameInput');
  private readonly subtitleInputRef = viewChild<ElementRef<HTMLInputElement>>('subtitleInput');

  /** Emitted when profile header is clicked (for edit) */
  readonly editClick = output<void>();

  /** Emitted when a quick action is clicked */
  readonly quickActionClick = output<ProfileQuickAction>();

  /** Emitted when a danger action is clicked */
  readonly dangerActionClick = output<ProfileDangerAction>();

  protected startNameEdit(): void {
    this.isEditingName.set(true);
    // Focus the input after it renders
    queueMicrotask(() => this.nameInputRef()?.nativeElement.focus());
  }

  protected commitNameEdit(value: string): void {
    const trimmed = value.trim();
    if (trimmed && trimmed !== this.avatarName()) {
      this.nameChange.emit(trimmed);
    }
    this.isEditingName.set(false);
  }

  protected cancelNameEdit(): void {
    this.isEditingName.set(false);
  }

  protected startSubtitleEdit(): void {
    this.isEditingSubtitle.set(true);
    queueMicrotask(() => this.subtitleInputRef()?.nativeElement.focus());
  }

  protected commitSubtitleEdit(value: string): void {
    const trimmed = value.trim();
    if (trimmed !== (this.subtitle() ?? '')) {
      this.subtitleChange.emit(trimmed);
    }
    this.isEditingSubtitle.set(false);
  }

  protected cancelSubtitleEdit(): void {
    this.isEditingSubtitle.set(false);
  }
}
